/**
 * AI Service - Free AI Features using Hugging Face Inference API
 * No API key required for public models
 */

// Hugging Face Inference API endpoints (free, no API key needed)
const HUGGINGFACE_API = 'https://api-inference.huggingface.co/models';

// Model endpoints (free public models)
const MODELS = {
  // Text generation and completion
  TEXT_GENERATION: 'gpt2', // Free GPT-2 model
  TEXT_SUMMARIZATION: 'facebook/bart-large-cnn',
  
  // Sentiment analysis
  SENTIMENT: 'cardiffnlp/twitter-roberta-base-sentiment-latest',
  
  // Text classification
  TEXT_CLASSIFICATION: 'distilbert-base-uncased-finetuned-sst-2-english',
  
  // Question answering
  QA: 'deepset/roberta-base-squad2',
  
  // Translation (if needed)
  TRANSLATION: 'Helsinki-NLP/opus-mt-en-es',
};

/**
 * Analyze sentiment of text (positive, negative, neutral)
 */
export const analyzeSentiment = async (text) => {
  try {
    if (!text || text.trim().length === 0) {
      return { label: 'neutral', score: 0.5 };
    }

    const response = await fetch(`${HUGGINGFACE_API}/${MODELS.SENTIMENT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ inputs: text }),
    });

    if (!response.ok) {
      throw new Error('Sentiment analysis failed');
    }

    const data = await response.json();
    
    // Handle array response
    if (Array.isArray(data) && data[0] && data[0].length > 0) {
      const result = data[0][0];
      return {
        label: result.label.toLowerCase(),
        score: result.score,
      };
    }
    
    return { label: 'neutral', score: 0.5 };
  } catch (error) {
    console.error('Sentiment analysis error:', error);
    // Fallback to neutral
    return { label: 'neutral', score: 0.5 };
  }
};

/**
 * Generate text suggestions/completions
 */
export const generateTextSuggestion = async (prompt, maxLength = 50) => {
  try {
    const response = await fetch(`${HUGGINGFACE_API}/${MODELS.TEXT_GENERATION}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        inputs: prompt,
        parameters: {
          max_length: maxLength,
          return_full_text: false,
        },
      }),
    });

    if (!response.ok) {
      throw new Error('Text generation failed');
    }

    const data = await response.json();
    
    if (Array.isArray(data) && data[0] && data[0].generated_text) {
      return data[0].generated_text.trim();
    }
    
    return '';
  } catch (error) {
    console.error('Text generation error:', error);
    return '';
  }
};

/**
 * Summarize text
 */
export const summarizeText = async (text, maxLength = 50) => {
  try {
    if (!text || text.trim().length === 0) {
      return '';
    }

    const response = await fetch(`${HUGGINGFACE_API}/${MODELS.TEXT_SUMMARIZATION}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        inputs: text,
        parameters: {
          max_length: maxLength,
          min_length: 10,
        },
      }),
    });

    if (!response.ok) {
      throw new Error('Text summarization failed');
    }

    const data = await response.json();
    
    if (Array.isArray(data) && data[0] && data[0].summary_text) {
      return data[0].summary_text.trim();
    }
    
    return '';
  } catch (error) {
    console.error('Text summarization error:', error);
    return '';
  }
};

/**
 * Classify text (e.g., spam detection, intent classification)
 */
export const classifyText = async (text) => {
  try {
    if (!text || text.trim().length === 0) {
      return { label: 'UNKNOWN', score: 0 };
    }

    const response = await fetch(`${HUGGINGFACE_API}/${MODELS.TEXT_CLASSIFICATION}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ inputs: text }),
    });

    if (!response.ok) {
      throw new Error('Text classification failed');
    }

    const data = await response.json();
    
    if (Array.isArray(data) && data[0] && data[0].length > 0) {
      const result = data[0][0];
      return {
        label: result.label,
        score: result.score,
      };
    }
    
    return { label: 'UNKNOWN', score: 0 };
  } catch (error) {
    console.error('Text classification error:', error);
    return { label: 'UNKNOWN', score: 0 };
  }
};

/**
 * Analyze comment for automation (sentiment + intent)
 */
export const analyzeComment = async (commentText) => {
  try {
    const [sentiment, classification] = await Promise.all([
      analyzeSentiment(commentText),
      classifyText(commentText),
    ]);

    // Determine intent based on keywords and sentiment
    const lowerText = commentText.toLowerCase();
    let intent = 'neutral';
    
    if (lowerText.includes('interested') || lowerText.includes('want') || lowerText.includes('buy')) {
      intent = 'interested';
    } else if (lowerText.includes('?') || lowerText.includes('how') || lowerText.includes('what')) {
      intent = 'question';
    } else if (lowerText.includes('love') || lowerText.includes('amazing') || lowerText.includes('great')) {
      intent = 'positive_feedback';
    } else if (lowerText.includes('bad') || lowerText.includes('hate') || lowerText.includes('terrible')) {
      intent = 'complaint';
    }

    return {
      sentiment,
      classification,
      intent,
      isSpam: classification.label === 'NEGATIVE' && sentiment.score < 0.3,
      shouldRespond: intent !== 'neutral' && !(classification.label === 'NEGATIVE' && sentiment.score < 0.3),
    };
  } catch (error) {
    console.error('Comment analysis error:', error);
    return {
      sentiment: { label: 'neutral', score: 0.5 },
      classification: { label: 'UNKNOWN', score: 0 },
      intent: 'neutral',
      isSpam: false,
      shouldRespond: true,
    };
  }
};

/**
 * Generate personalized message suggestions
 */
export const generateMessageSuggestions = async (context) => {
  const baseTemplate = "Hi {{name}}/ subscriberName, thanks for showing the interest in the product. Here is the product link {{productlink}}.";
  const normalizedTemplate = baseTemplate.replace("{{productlink}}", "{{product_link}}");

  try {
    const { recipientName, productLink, name, productName, tone = 'friendly' } = context;
    const safeProductLink = productLink || "{{product_link}}";
    const safeName = recipientName || name || "subscriberName";

    const prompts = [
      `Write a ${tone} message to ${safeName} about ${productName || 'our product'}`,
      `Create a personalized DM for ${safeName} interested in ${productName || 'our offer'}`,
      `Generate a ${tone} message thanking ${safeName} for their interest`,
    ];

    const aiSuggestion = await generateTextSuggestion(prompts[0], 100);

    const fallbackSuggestions = [
      baseTemplate,
      normalizedTemplate,
      `Hi ${safeName}, thanks for showing the interest in the product. Here is the product link ${safeProductLink}.`,
      `Hi ${safeName}! Thanks for showing your interest in ${productName || 'our product'}. Here is the product link ${safeProductLink}.`,
    ];

    const suggestions = [
      ...(aiSuggestion && aiSuggestion.length >= 10 ? [aiSuggestion.trim()] : []),
      ...fallbackSuggestions,
    ];

    return [...new Set(suggestions)].filter(Boolean);
  } catch (error) {
    console.error('Message generation error:', error);
    return [
      baseTemplate,
      normalizedTemplate,
      "Hi {{name}}, thanks for showing the interest in the product. Here is the product link {{product_link}}.",
    ];
  }
};

/**
 * Analyze dashboard data and generate insights
 */
export const generateDashboardInsights = async (stats) => {
  try {
    const insights = [];
    
    // Analyze conversion rate
    if (stats.conversionRate > 0) {
      if (stats.conversionRate < 2) {
        insights.push({
          type: 'warning',
          title: 'Low Conversion Rate',
          message: `Your conversion rate is ${stats.conversionRate.toFixed(2)}%. Consider optimizing your link placement and messaging.`,
          suggestion: 'Try sharing links in stories, posts, and bio for better visibility.',
        });
      } else if (stats.conversionRate > 5) {
        insights.push({
          type: 'success',
          title: 'Excellent Conversion Rate',
          message: `Your conversion rate of ${stats.conversionRate.toFixed(2)}% is above average!`,
          suggestion: 'Keep up the great work! Consider scaling successful strategies.',
        });
      }
    }

    // Analyze earnings
    if (stats.totalEarnings > 0) {
      const monthlyEarnings = stats.totalEarnings;
      if (monthlyEarnings > 1000) {
        insights.push({
          type: 'success',
          title: 'Great Earnings!',
          message: `You've earned $${monthlyEarnings.toFixed(2)} this month.`,
          suggestion: 'Consider diversifying your campaigns for even more growth.',
        });
      }
    }

    // Analyze link performance
    if (stats.totalLinks > 0 && stats.totalVisits > 0) {
      const avgVisitsPerLink = stats.totalVisits / stats.totalLinks;
      if (avgVisitsPerLink < 10) {
        insights.push({
          type: 'info',
          title: 'Link Performance',
          message: `Average ${avgVisitsPerLink.toFixed(1)} visits per link.`,
          suggestion: 'Try promoting links more actively on your social media.',
        });
      }
    }

    // Analyze campaign engagement
    if (stats.activeCampaigns > 0) {
      insights.push({
        type: 'info',
        title: 'Active Campaigns',
        message: `You have ${stats.activeCampaigns} active campaign${stats.activeCampaigns > 1 ? 's' : ''}.`,
        suggestion: 'Focus on campaigns with highest conversion rates.',
      });
    }

    return insights;
  } catch (error) {
    console.error('Insights generation error:', error);
    return [];
  }
};

/**
 * Smart keyword suggestions for automation
 */
export const suggestKeywords = async (productDescription, campaignType) => {
  try {
    // Analyze product description to extract key terms
    const words = productDescription
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 3);

    // Common interest keywords
    const interestKeywords = ['interested', 'want', 'buy', 'purchase', 'need', 'looking'];
    
    // Question keywords
    const questionKeywords = ['how', 'what', 'where', 'when', 'price', 'cost'];
    
    // Positive feedback keywords
    const positiveKeywords = ['love', 'amazing', 'great', 'awesome', 'beautiful', 'nice'];
    
    // Combine and deduplicate
    const suggestedKeywords = [
      ...new Set([
        ...interestKeywords.slice(0, 3),
        ...questionKeywords.slice(0, 2),
        ...positiveKeywords.slice(0, 2),
        ...words.slice(0, 5), // Top words from description
      ]),
    ];

    return suggestedKeywords.slice(0, 10);
  } catch (error) {
    console.error('Keyword suggestion error:', error);
    return ['interested', 'want', 'buy', 'how', 'price', 'love', 'amazing'];
  }
};

/**
 * Check if browser supports Speech Recognition
 */
export const isSpeechRecognitionSupported = () => {
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

/**
 * Initialize Speech Recognition (free browser API)
 */
export const initSpeechRecognition = (onResult, onError) => {
  if (!isSpeechRecognitionSupported()) {
    throw new Error('Speech recognition not supported in this browser');
  }

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = new SpeechRecognition();
  
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.lang = 'en-US';

  recognition.onresult = (event) => {
    const transcript = event.results[0][0].transcript;
    if (onResult) onResult(transcript);
  };

  recognition.onerror = (event) => {
    if (onError) onError(event.error);
  };

  return recognition;
};

export default {
  analyzeSentiment,
  generateTextSuggestion,
  summarizeText,
  classifyText,
  analyzeComment,
  generateMessageSuggestions,
  generateDashboardInsights,
  suggestKeywords,
  isSpeechRecognitionSupported,
  initSpeechRecognition,
};

